import { useEffect, useState } from "react";
import { initMetaPixel, revokeMetaPixel } from "@/lib/metaPixel";

/**
 * localStorage scope note (security review):
 * We store ONLY non-sensitive UX preferences here — never auth tokens or PII.
 * Auth lives in an httpOnly cookie (set by /api/auth/session). The keys below are:
 *   s2s_consent_v1     — {essential, analytics, ts} (cookie preference)
 *   s2s_analytics_enabled — boolean flag mirrored for PostHog SDK
 *   s2s_theme           — "light" | "dark"
 *   s2s_resume_v1       — {id, mode, status, ts} (assessment-resume pointer, not the answers themselves)
 * Any XSS that already has DOM access could read these, but the disclosure surface is intentional UX
 * state, not credentials.
 */

const KEY = "s2s_consent_v1";
const POSTHOG_KEY = "s2s_analytics_enabled";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch (e) {
    console.warn("consent parse failed", e);
    return null;
  }
}

export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) setOpen(true);
    if (c && c.analytics === true) {
      initMetaPixel();
    }
    if (c && c.analytics === false) {
      try { window.posthog?.opt_out_capturing?.(); } catch (e) { console.warn("posthog opt-out failed", e); }
    }
  }, []);

  const save = (analytics) => {
    const v = { essential: true, analytics, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    localStorage.setItem(POSTHOG_KEY, analytics ? "1" : "0");
    if (analytics) {
      try { window.posthog?.opt_in_capturing?.(); } catch (e) { console.warn("posthog opt-in failed", e); }
      initMetaPixel();
    } else {
      try { window.posthog?.opt_out_capturing?.(); } catch (e) { console.warn("posthog opt-out failed", e); }
      revokeMetaPixel();
    }
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
    >
      <div className="p-5">
        <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Your privacy</p>
        <p className="mt-1.5 text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">
          Essential cookies keep the app running. With your consent, we also use anonymous analytics
          and the Meta Pixel for advertising measurement to improve our service. Change this anytime in our{" "}
          <a href="/privacy" className="text-teal-700 dark:text-teal-400 underline underline-offset-2">Privacy notice</a>.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => save(true)}
            data-testid="consent-accept-all"
            className="inline-flex h-9 px-4 items-center rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
          >
            Accept all
          </button>
          <button
            onClick={() => save(false)}
            data-testid="consent-essential-only"
            className="inline-flex h-9 px-4 items-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
